import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { updateTimelineBounds } from "../thunks/timelineSettingsThunk";
import {
  Booking,
  BookingDTO,
  BookingPayload,
  BookingWithoutInvoiceDTO,
} from "../types/dtos";

export const bookingsApi = createApi({
  reducerPath: "bookingsApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api/bookings/" }),
  tagTypes: ["Bookings", "Animals", "Customers"],
  endpoints: (builder) => ({
    getCapacityByDay: builder.query<
      {
        date: string;
        am: number;
        pm: number;
        bySize?: Record<string, { am: number; pm: number }>;
        notCastrated?: { am: number; pm: number };
        byGender?: {
          female?: { am: number; pm: number };
          maleCastrated?: { am: number; pm: number };
          maleNotCastrated?: { am: number; pm: number };
        };
      }[],
      {
        timelineStart: string;
        timelineEnd: string;
        animalSpeciesId?: number;
        siteId?: number;
      }
    >({
      query: ({ timelineStart, timelineEnd, animalSpeciesId }) => ({
        url: `capacity`,
        params: {
          timelineStart,
          timelineEnd,
          ...(animalSpeciesId ? { animalSpeciesId } : {}),
        },
      }),
      providesTags: [{ type: "Bookings", id: "CAPACITY" }],
    }),
    getSpaceCapacityByDay: builder.query<
      { maxOverall: number; days: { date: string; am: number; pm: number; overall?: number }[] },
      {
        spaceId: number;
        start: string;
        end: string;
        animalSizeId?: number;
        excludeBookingId?: number;
        siteId?: number;
      }
    >({
      query: ({ spaceId, start, end, animalSizeId, excludeBookingId }) => ({
        url: `spacecapacity`,
        params: {
          spaceId,
          start,
          end,
          ...(animalSizeId != null ? { animalSizeId } : {}),
          ...(excludeBookingId != null ? { excludeBookingId } : {}),
        },
      }),
      providesTags: [{ type: "Bookings", id: "SPACE_CAPACITY" }],
    }),
    getBookingsWithoutInvoice: builder.query<
      BookingWithoutInvoiceDTO[],
      { siteId?: number } | void
    >({
      query: () => ({ url: `noinvoice/` }),
      transformResponse: (res: any): BookingWithoutInvoiceDTO[] =>
        Array.isArray(res?.bookingsWithoutInvoices)
          ? (res.bookingsWithoutInvoices as BookingWithoutInvoiceDTO[])
          : [],
      providesTags: [{ type: "Bookings", id: "NO_INVOICE" }],
    }),
    setManualInvoiceCompletion: builder.mutation<
      { ok: boolean },
      {
        bookingId: number;
        bookingType: "PENSION" | "SERVICE";
        completed: boolean;
      }
    >({
      async queryFn(body) {
        try {
          const response = await fetch(
            "/api/invoices/open-bookings/manual-completion",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            }
          );
          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: await response.json().catch(() => ({})),
              } as any,
            };
          }
          const data = (await response.json()) as { ok: boolean };
          return { data };
        } catch (error) {
          return { error: { status: "FETCH_ERROR", error } as any };
        }
      },
      invalidatesTags: [{ type: "Bookings", id: "NO_INVOICE" }],
    }),
    patchMultipleBookings: builder.mutation<
      { updated: number },
      { ids: string[]; data: Partial<Booking> }
    >({
      async queryFn({ ids, data }, _api, _extraOptions, baseQuery) {
        const results = await Promise.all(
          ids.map((id) =>
            baseQuery({
              url: `booking/${id}`,
              method: "PATCH",
              body: data,
            })
          )
        );
        // Detect any errors returned by baseQuery (it resolves, doesn't throw)
        const anyError = results.find((r: any) => r && r.error);
        if (anyError) {
          return { error: (anyError as any).error } as any;
        }
        return { data: { updated: ids.length } } as any;
      },
      invalidatesTags: [{ type: "Bookings", id: "LIST" }, { type: "Bookings", id: "CAPACITY" }],
    }),
    getAllBookings: builder.query<
      BookingDTO[],
      {
        timelineStart: string;
        animalSpeciesId: number;
        timelineEnd?: string;
        siteId?: number;
      }
    >({
      query: ({ timelineStart, animalSpeciesId, timelineEnd }) => ({
        url: "",
        params: {
          timelineStart,
          animalSpeciesId,
          ...(timelineEnd ? { timelineEnd } : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              { type: "Bookings", id: "LIST" },
              ...result.map((b) => ({
                type: "Bookings" as const,
                id: String(b.AutoID),
              })),
            ]
          : [{ type: "Bookings", id: "LIST" }],
      onQueryStarted: async (
        { timelineStart, animalSpeciesId, siteId },
        { dispatch, queryFulfilled }
      ) => {
        try {
          await queryFulfilled;
          dispatch(updateTimelineBounds({ timelineStart, animalSpeciesId, siteId }));
        } catch {
          // optional
        }
      },
    }),
    getBookingsBySearchTerm: builder.query<
      BookingDTO[],
      { term: string; timelineStart: string; animalSpeciesId: number; siteId?: number }
    >({
      query: ({ term, timelineStart, animalSpeciesId }) => ({
        url: `${term}`,
        params: { timelineStart, animalSpeciesId },
      }),
    }),
    getBookingsByCustomer: builder.query<any[], number | string>({
      query: (customerId) => ({ url: `bycustomer/${customerId}` }),
      transformResponse: (res: any) => res?.bookings ?? [],
      providesTags: (result, _error, customerId) => {
        const tags: { type: "Bookings" | "Customers"; id: string }[] = [
          { type: "Bookings", id: "LIST" },
          { type: "Customers", id: `BOOKINGS_${customerId}` },
        ];
        if (Array.isArray(result)) {
          tags.push(
            ...result
              .map((booking) => booking?.AutoID ?? booking?.id)
              .filter((id) => id != null)
              .map((id) => ({ type: "Bookings" as const, id: String(id) }))
          );
        }
        return tags;
      },
    }),
    getBookingById: builder.query<BookingDTO, string>({
      query: (id) => `booking/${id}`,
      providesTags: (result, error, id) => [{ type: "Bookings", id }],
    }),

    createBooking: builder.mutation<BookingPayload, Partial<BookingPayload>>({
      query: (body) => ({
        url: "",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Bookings", id: "LIST" },
        { type: "Bookings", id: "CAPACITY" },
        { type: "Bookings", id: "NO_INVOICE" },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { bumpReloadSeq } = await import(
            "@/app/_store/slices/calendarUiSlice"
          );
          dispatch(bumpReloadSeq());
        } catch {}
      },
    }),

    updateBooking: builder.mutation<
      Booking,
      { id: string; data: Partial<Booking> }
    >({
      query: ({ id, data }) => ({
        url: `booking/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Bookings", id },
        { type: "Bookings", id: "LIST" },
        { type: "Bookings", id: "CAPACITY" },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { bumpReloadSeq } = await import(
            "@/app/_store/slices/calendarUiSlice"
          );
          dispatch(bumpReloadSeq());
        } catch {}
      },
    }),

    deleteBooking: builder.mutation<void, string>({
      query: (id) => ({
        url: `booking/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Bookings", id },
        { type: "Bookings", id: "LIST" },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { bumpReloadSeq } = await import(
            "@/app/_store/slices/calendarUiSlice"
          );
          dispatch(bumpReloadSeq());
        } catch {}
      },
    }),

    restoreBooking: builder.mutation<{ AutoID: number; BookingState_ID: number }, string>({
      query: (id) => ({
        url: `booking/${id}/restore`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Bookings", id },
        { type: "Bookings", id: "LIST" },
        { type: "Bookings", id: "CAPACITY" },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const { bumpReloadSeq } = await import(
            "@/app/_store/slices/calendarUiSlice"
          );
          dispatch(bumpReloadSeq());
        } catch {}
      },
    }),

    getOverlappingBookings: builder.query<
      BookingDTO[],
      { customerId: number; start: string; end: string; excludeId?: number }
    >({
      query: ({ customerId, start, end, excludeId }) => {
        const params = new URLSearchParams({
          customerId: customerId.toString(),
          start: start,
          end: end,
        });
        if (excludeId) params.append("excludeId", excludeId.toString());
        return `overlaps?${params.toString()}`;
      },
    }),

    getSpaceOptions: builder.query<
      import("../types/dtos").SpaceOption[],
      { animalSizeId?: number; animalSpeciesId?: number; date?: string; start?: string; end?: string; withDays?: boolean; excludeBookingId?: number } | void
    >({
      query: (arg) => {
        const params = new URLSearchParams();
        const size = (arg as any)?.animalSizeId;
        const species = (arg as any)?.animalSpeciesId;
        const date = (arg as any)?.date;
        const start = (arg as any)?.start;
        const end = (arg as any)?.end;
        const withDays = (arg as any)?.withDays;
        const excludeBookingId = (arg as any)?.excludeBookingId;
        if (typeof size === "number" && !isNaN(size))
          params.set("animalSizeId", String(size));
        if (typeof species === "number" && !isNaN(species))
          params.set("animalSpeciesId", String(species));
        if (typeof date === "string" && date) params.set("date", date);
        if (typeof start === "string" && start) params.set("start", start);
        if (typeof end === "string" && end) params.set("end", end);
        if (withDays) params.set("withDays", "1");
        if (typeof excludeBookingId === "number" && !isNaN(excludeBookingId))
          params.set("excludeBookingId", String(excludeBookingId));
        const qs = params.toString();
        return `spaceoptions${qs ? `?${qs}` : ""}`;
      },
      transformResponse: (res: any) => (Array.isArray(res) ? res : []),
    }),

    checkHasInvoiceDraft: builder.query<boolean, string>({
      query: (bookingId) => `hasinvoiceindraft/${bookingId}`,
    }),
    // moved getInvoiceForBooking to invoicesApi
    // moved animals/customers/options to respective APIs
  }),
});

export const {
  useGetCapacityByDayQuery,
  useGetSpaceCapacityByDayQuery,
  useGetAllBookingsQuery,
  useGetBookingsBySearchTermQuery,
  useGetBookingsByCustomerQuery,
  useGetBookingByIdQuery,
  useCreateBookingMutation,
  useUpdateBookingMutation,
  useDeleteBookingMutation,
  useRestoreBookingMutation,
  useGetBookingsWithoutInvoiceQuery,
  useSetManualInvoiceCompletionMutation,
  useGetOverlappingBookingsQuery,
  useGetSpaceOptionsQuery,
  useLazyGetSpaceOptionsQuery,
  useCheckHasInvoiceDraftQuery,
  useLazyCheckHasInvoiceDraftQuery,
  usePatchMultipleBookingsMutation,
} = bookingsApi;
